import React, { useEffect, useRef, useState } from 'react';
import { Table, Button, Modal, notification, Input, Space, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import {colors} from '../theme';
import { useNavigate } from 'react-router-dom';
import { warningsApi, Warning } from '../apiCalls/warningsApi';
import type { TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import TableFiltersBar, { applyFiltersToColumns, useTableFilters } from '../components/TableFilters';
import PageTitle from '../components/PageTitle';

const WarningsList: React.FC = () => {
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<Warning[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const searchInput = useRef<any>(null);
  const { filters, handleTableChange, clearAllFilters, removeFilterValue } = useTableFilters<Warning>();

  useEffect(() => {
    fetchWarnings();
  }, []);

  const fetchWarnings = async () => {
    setLoading(true);
    const warnings = await warningsApi.getWarnings();
    setDataSource(warnings);
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    try {
      setDeleting(id);
      await warningsApi.deleteWarning(id);
      notification.success({
        message: 'Warning Deleted',
        description: 'The warning has been successfully removed.',
        duration: 3,
      });
      fetchWarnings();
    } catch (error: any) {
      notification.error({
        message: 'Error while deleting',
        description: error.message,
        duration: 5,
      });
    } finally {
      setDeleting(null);
    }
  };

  const showDeleteConfirm = (record: Warning) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this warning?',
      content:
          'If this warning is in use in any product or ingredient, it cannot be removed.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(record.id);
      },
    });
  };

  const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps['confirm'], dataIndex: string) => {
    confirm();
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex: string): TableColumnType<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              close();
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'left' as const,
      width: 100,
      align: 'center' as const,
      render: (_: any, record: Warning) => (
          <Space size={0}>
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                style={{color: colors.primary}}
                icon={<EditOutlined />}
                onClick={() => navigate(`/warnings/${record.id}/edit`)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                danger
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => showDeleteConfirm(record)}
                loading={deleting === record.id}
              />
            </Tooltip>
          </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
    },
  ];
  const tableColumns = applyFiltersToColumns(columns, filters);

  return (
      <div style={{padding: '24px 32px'}}>
        <PageTitle title="List of Warnings" />
        <Button
            type="primary"
            onClick={() => navigate('/warnings/new')}
            style={{marginBottom: 16}}
        >
          Add New Warning
        </Button>
        <TableFiltersBar
          columns={tableColumns}
          filters={filters}
          onClearAll={clearAllFilters}
          onRemove={removeFilterValue}
        />
        <Table
            dataSource={dataSource}
            columns={tableColumns}
            rowKey="id"
            loading={loading}
            pagination={{defaultPageSize: 50, pageSizeOptions: [10, 25, 50]}}
            scroll={{x: 'max-content', y: 'calc(100vh - 300px)'}}
            onChange={handleTableChange}
        />
      </div>
  );
};

export default WarningsList;
